/**
 * Allow-list filter for HTML inside article Markdown (a rehype plugin, run
 * right after rehype-raw in ArticleBody).
 *
 * Articles imported from a PDF carry HTML for what Markdown cannot express -
 * tables with merged and coloured cells, coloured blocks, sized pictures - and
 * any author can type HTML as well. Rendered as it stands, `<img onerror>` or
 * `<iframe>` would run for every reader. So only the tags and attributes
 * below survive; `style` keeps only colours and text alignment. Unknown tags
 * are unwrapped (their text stays), dangerous ones are dropped with their
 * content. URLs are checked afterwards by react-markdown's urlTransform.
 */

const DROP_WITH_CONTENT = new Set([
  'script', 'style', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet',
  'form', 'input', 'button', 'select', 'textarea', 'option', 'template',
  'link', 'meta', 'base', 'noscript', 'svg', 'math',
]);

const COMMON = ['title', 'lang', 'dir'];
const CELL = [...COMMON, 'rowSpan', 'colSpan', 'style', 'align', 'valign', 'scope'];

// Tag -> allowed hast property names (camelCase, as rehype-raw produces them).
const ALLOWED = {
  p: [...COMMON, 'style', 'align'], div: [...COMMON, 'style', 'align'], span: [...COMMON, 'style'],
  br: [], hr: [],
  h1: COMMON, h2: COMMON, h3: COMMON, h4: COMMON, h5: COMMON, h6: COMMON,
  strong: COMMON, b: COMMON, em: COMMON, i: COMMON, u: COMMON, s: COMMON, del: COMMON,
  ins: COMMON, mark: COMMON, sub: COMMON, sup: COMMON, small: COMMON, code: COMMON,
  pre: COMMON, blockquote: COMMON, kbd: COMMON, abbr: COMMON,
  ul: COMMON, ol: [...COMMON, 'start'], li: COMMON, dl: COMMON, dt: COMMON, dd: COMMON,
  table: [...COMMON, 'style'], thead: COMMON, tbody: COMMON, tfoot: COMMON,
  tr: [...COMMON, 'style'], td: CELL, th: CELL, caption: COMMON, colgroup: COMMON,
  col: ['span', 'style'],
  a: [...COMMON, 'href'],
  img: [...COMMON, 'src', 'alt', 'width', 'height', 'style', 'className'],
  figure: COMMON, figcaption: COMMON,
  section: COMMON,
};

// GFM task lists render <input type="checkbox" disabled>; keep exactly that.
function isTaskCheckbox(node) {
  return node.tagName === 'input'
    && node.properties?.type === 'checkbox'
    && node.properties?.disabled !== undefined;
}

const STYLE_PROPERTIES = new Set([
  'color', 'background-color', 'background', 'text-align', 'vertical-align',
  'font-weight', 'font-style', 'text-decoration', 'width', 'height', 'max-width',
]);
const STYLE_VALUE = /^[#a-z0-9.,%()\s-]+$/i;

function safeStyle(style) {
  if (typeof style !== 'string') {
    return null;
  }

  const kept = style
    .split(';')
    .map((declaration) => declaration.split(':'))
    .filter((parts) => parts.length === 2)
    .map(([name, value]) => [name.trim().toLowerCase(), value.trim()])
    .filter(([name, value]) => STYLE_PROPERTIES.has(name)
      && STYLE_VALUE.test(value)
      && !/url\s*\(|expression|javascript/i.test(value))
    .map(([name, value]) => `${name}: ${value}`);

  return kept.length ? kept.join('; ') : null;
}

// Classes the platform itself puts on HTML it generates - the PDF import
// marks pictures in table cells. Any other class is dropped: an author could
// otherwise borrow the interface's own styles (overlays, fixed positioning).
const ALLOWED_CLASSES = {
  img: new Set(['pdf-import-icon', 'pdf-import-image']),
};

function safeClasses(tagName, className) {
  const allowed = ALLOWED_CLASSES[tagName];
  const names = Array.isArray(className) ? className : String(className).split(/\s+/);
  const kept = names.filter((name) => allowed?.has(name));

  return kept.length ? kept : null;
}

function cleanProperties(tagName, properties = {}) {
  const allowed = ALLOWED[tagName] ?? [];
  const cleaned = {};

  for (const name of allowed) {
    if (properties[name] === undefined || properties[name] === null) {
      continue;
    }

    if (name === 'style') {
      const style = safeStyle(properties.style);
      if (style) cleaned.style = style;
      continue;
    }

    if (name === 'className') {
      const classes = safeClasses(tagName, properties.className);
      if (classes) cleaned.className = classes;
      continue;
    }

    cleaned[name] = properties[name];
  }

  return cleaned;
}

function cleanChildren(children) {
  const out = [];

  for (const node of children ?? []) {
    if (node.type === 'comment' || node.type === 'doctype') {
      continue;
    }

    if (node.type !== 'element') {
      out.push(node);
      continue;
    }

    const tag = node.tagName.toLowerCase();

    if (isTaskCheckbox(node)) {
      out.push({ ...node, properties: { type: 'checkbox', checked: Boolean(node.properties.checked), disabled: true } });
      continue;
    }

    if (DROP_WITH_CONTENT.has(tag)) {
      continue;
    }

    if (!ALLOWED[tag]) {
      out.push(...cleanChildren(node.children));
      continue;
    }

    out.push({
      ...node,
      tagName: tag,
      properties: cleanProperties(tag, node.properties),
      children: cleanChildren(node.children),
    });
  }

  return out;
}

/** rehype plugin: `rehypePlugins={[rehypeRaw, rehypeSafeHtml]}`. */
export default function rehypeSafeHtml() {
  return (tree) => {
    tree.children = cleanChildren(tree.children);
  };
}
